import { memo, useLayoutEffect, useMemo, useRef } from "react";
import { flexRender } from "@tanstack/react-table";
import { isEqual } from "lodash";

import { sanitizeCSSVarName } from "components/helpers";
import { SELECT_COLUMN_ID } from "constants/Constants";
import { BaseTableV3CellCopyButton } from "components/BaseTableV3/BaseTableV3CellCopyButton";
import {
  getCellDisplayText,
  isActionsColumn,
} from "components/BaseTableV3/table-helpers";
import { getCommonPinningStyles } from "./BaseTableV3";

function BaseTableV3CellInner({ cell, columnPinning, copyTooltipHandle }) {
  const cellRef = useRef(null);

  const isPinned = cell.column.getIsPinned();
  const isCopyable =
    cell.column.id !== SELECT_COLUMN_ID &&
    !isActionsColumn(cell.column.columnDef) &&
    cell.column.columnDef.meta?.copyable !== false;

  const sanitizedColKey = useMemo(() => {
    return sanitizeCSSVarName(`--col-${cell.column.id}-size`);
  }, [cell.column.id]);

  const style = useMemo(
    () =>
      isPinned
        ? getCommonPinningStyles(cell.column)
        : { width: `calc(var(${sanitizedColKey}) * 1px)` },
    [isPinned, cell.column, sanitizedColKey, columnPinning]
  );

  // The copy button adds no text (aria-label only, portaled tooltip), so the
  // whole cell can be read without a wrapper that would break td styling.
  const getText = () => getCellDisplayText(cellRef.current);

  // The text only exists once the column's cell renderer has mounted, so it is
  // read from the DOM. Flagging the td directly hides the copy button of an
  // empty cell (and drops it from the tab order) without re-rendering the cell.
  const markEmptyCell = () =>
    cellRef.current.toggleAttribute("data-empty", !getText());

  useLayoutEffect(() => {
    if (isCopyable) markEmptyCell();
  });

  // The inline style's `position: sticky` on pinned cells wins over tw:relative.
  // The absolutely positioned button goes first so the cell content stays the
  // last child: Semantic UI drops a paragraph's bottom margin only on :last-child.
  return (
    <td
      key={cell.id}
      ref={cellRef}
      style={style}
      className="tw:group tw:relative"
      // Re-measured on hover too, for cell content that loads after mount.
      onPointerEnter={isCopyable ? markEmptyCell : undefined}
    >
      {isCopyable && (
        <BaseTableV3CellCopyButton
          getText={getText}
          tooltipHandle={copyTooltipHandle}
        />
      )}
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </td>
  );
}

export const areCellPropsEqual = (prev, next) => {
  const prevCell = prev.cell;
  const nextCell = next.cell;

  if (prevCell.id !== nextCell.id) return false;
  if (prevCell.row.original !== nextCell.row.original) return false;
  if (
    prevCell.column.id === SELECT_COLUMN_ID &&
    prev.isSelected !== next.isSelected
  )
    return false;
  // getIsPinned() reads live state off a shared column ref, so compare the prop.
  if (!isEqual(prev.columnPinning, next.columnPinning)) return false;

  return true;
};

export const BaseTableV3Cell = memo(BaseTableV3CellInner, areCellPropsEqual);
